import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, CalendarCheck, CheckCircle2, Loader2 } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { SCHEDULING } from "@/constants/testIds";
import { pushEvent } from "@/lib/faro";

// Sem REACT_APP_BACKEND_URL (produção), as chamadas ficam relativas e caem na
// Pages Function same-origin (frontend/functions/api/[[path]].js), que faz proxy
// para o Cloud Run.
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const EMPTY = { name: "", email: "", company: "", phone: "", message: "" };

// Passos do fluxo: formulário → (qualificado) escolher horário → confirmado / (não) contato manual.
const STEP = { FORM: "form", BOOKING: "booking", DONE: "done", MANUAL: "manual" };

const slotLabel = (iso) =>
  new Date(iso).toLocaleString("pt-BR", {
    weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(STEP.FORM);
  const [token, setToken] = useState("");
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Preencha nome, e-mail e mensagem.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "lead_submit", form: "contato" });
      pushEvent("lead_submit", { form: "contato" });
      if (data.qualified && data.booking_available && data.booking_token) {
        setToken(data.booking_token);
        const { data: avail } = await axios.get(`${API}/slots`, { params: { token: data.booking_token } });
        setSlots(avail.slots || []);
        setStep(STEP.BOOKING);
      } else {
        setStep(STEP.MANUAL);
      }
    } catch (err) {
      toast.error("Não foi possível enviar. Tente novamente em instantes.");
    } finally {
      setLoading(false);
    }
  };

  const confirm = async () => {
    if (!selected) {
      toast.error("Escolha um horário.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/book`, { token, slot_start: selected });
      setStep(STEP.DONE);
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("Esse horário acabou de ser preenchido. Escolha outro.");
        const { data: avail } = await axios.get(`${API}/slots`, { params: { token } });
        setSlots(avail.slots || []);
        setSelected("");
      } else {
        toast.error("Não foi possível agendar. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3.5 text-white placeholder-white/35 outline-none transition-colors duration-200 focus:border-bf-yellow/60";

  return (
    <section id="contato" className="relative py-24 md:py-36 scroll-mt-20" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-bf-surface p-8 md:p-14 lg:p-20">
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-bf-yellow/15 blur-3xl" />
          <div className="pointer-events-none absolute -left-24 -bottom-24 h-72 w-72 rounded-full bg-bf-teal/15 blur-3xl" />

          <div className="relative grid lg:grid-cols-2 gap-12 items-start">
            <Reveal>
              <Overline>Vamos conversar</Overline>
              <h2 className="mt-6 font-display text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05]">
                Pronto para se tornar uma empresa AI First?
              </h2>
              <p className="mt-5 text-white/60 text-lg leading-relaxed max-w-md">
                Conte um pouco sobre o seu negócio. Vamos mapear onde a IA gera mais valor e desenhar um plano sob medida.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              {step === STEP.FORM && (
                <form onSubmit={submit} className="grid gap-4" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input className={field} placeholder="Seu nome*" value={form.name} onChange={update("name")} data-testid="contact-name" />
                    <input className={field} placeholder="E-mail*" type="email" value={form.email} onChange={update("email")} data-testid="contact-email" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input className={field} placeholder="Empresa" value={form.company} onChange={update("company")} data-testid="contact-company" />
                    <input className={field} placeholder="Telefone" value={form.phone} onChange={update("phone")} data-testid="contact-phone" />
                  </div>
                  <textarea className={`${field} min-h-[130px] resize-none`} placeholder="Como podemos ajudar?*" value={form.message} onChange={update("message")} data-testid="contact-message" />
                  <button
                    type="submit"
                    disabled={loading}
                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-bf-yellow px-7 py-4 font-bold text-black transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
                    data-testid="contact-submit"
                  >
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <>Fale com um consultor <ArrowUpRight size={18} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>}
                  </button>
                </form>
              )}

              {step === STEP.BOOKING && (
                <div className="grid gap-5" data-testid={SCHEDULING.slots}>
                  <p className="text-white/80 text-lg">Ótimo, {form.name.split(" ")[0]}! Escolha o melhor horário para conversarmos:</p>
                  {slots.length === 0 ? (
                    <p className="text-white/50">Sem horários disponíveis no momento — entraremos em contato.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 max-h-64 overflow-y-auto pr-1">
                      {slots.map((iso) => (
                        <button
                          key={iso}
                          type="button"
                          onClick={() => setSelected(iso)}
                          data-testid={SCHEDULING.slotOption}
                          className={`rounded-xl border px-3 py-2.5 text-sm transition-colors ${selected === iso ? "border-bf-yellow bg-bf-yellow/15 text-white" : "border-white/10 bg-black/40 text-white/70 hover:border-white/30"}`}
                        >
                          {slotLabel(iso)}
                        </button>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={confirm}
                    disabled={loading || !selected}
                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-bf-yellow px-7 py-4 font-bold text-black transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
                    data-testid={SCHEDULING.confirmButton}
                  >
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <>Confirmar reunião <CalendarCheck size={18} /></>}
                  </button>
                </div>
              )}

              {step === STEP.DONE && (
                <div className="grid gap-4 rounded-2xl border border-bf-teal/30 bg-bf-teal/10 p-8" data-testid={SCHEDULING.success}>
                  <CheckCircle2 size={40} className="text-bf-teal" />
                  <h3 className="font-display text-2xl font-bold">Reunião confirmada!</h3>
                  <p className="text-white/70">Enviamos os detalhes para {form.email}. Até lá!</p>
                </div>
              )}

              {step === STEP.MANUAL && (
                <div className="grid gap-4 rounded-2xl border border-white/10 bg-black/30 p-8" data-testid={SCHEDULING.unqualified}>
                  <CheckCircle2 size={40} className="text-bf-yellow" />
                  <h3 className="font-display text-2xl font-bold">Recebemos sua mensagem!</h3>
                  <p className="text-white/70">Um consultor vai analisar seu contexto e falar com você em breve.</p>
                </div>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
